import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Any, Silent } from "@/__main__/data-model.mjs";

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const Build = {
  build_id: {
    primary_tags_f3pie: String,
  },
  build_tags: [String],
  patch: String,
  champion_id: Number,
  championId: Number,
  matchupChampionId: Number,
  rank_plus: String,
  build_tier: String,
  coreItems: [
    { itemIds: String, games: Number, wins: Number, pick_rate: Number },
  ],
  situationalItems: [
    {
      itemId: Number,
      games: Number,
      wins: Number,
      pick_rate: Number,
      averageIndex: Number,
      distinctive_score: Number,
      pick_rate_w_support_difference: Number,
      support_avg_pick_rate: Number,
    },
  ],
  startingItems: [
    { itemIds: [Number], games: Number, wins: Number, pick_rate: Number },
  ],
  runes: [
    {
      runeId: Number,
      treeId: Number,
      games: Number,
      wins: Number,
      pick_rate: Number,
      index: Number,
    },
  ],
  keystone: [
    {
      keystone_id: Number,
      treeId: Number,
      games: Number,
      wins: Number,
      pick_rate: Number,
    },
  ],
  shards: {
    defenseShard: [
      { shard_id: Number, games: Number, wins: Number, pick_rate: Number },
    ],
    flexShard: [
      { shard_id: Number, games: Number, wins: Number, pick_rate: Number },
    ],
    offenseShard: [
      { shard_id: Number, games: Number, wins: Number, pick_rate: Number },
    ],
  },
  summonerSpells: [
    {
      summonerSpellIds: [Number],
      games: Number,
      wins: Number,
      pick_rate: Number,
    },
  ],
  skillOrders: [
    { skillOrder: [Number], games: Number, wins: Number, pick_rate: Number },
  ],
  games: Number,
  wins: Number,
  pick_rate: Number,
  misc: Silent(Any),
  name_tags: [String],
} satisfies ModelDefinition;

const LolChampionBuildsModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Silent(Number),
          dataArray: [[String]],
          rowCount: Silent(Number),
          rowOffset: Silent(Number),
        },
      },
    },
  },
}).transform([Build], (data) => {
  const executeDatabricksQuery = data?.data?.executeDatabricksQuery;
  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const builds = list.map((b) => {
    const result: LeagueChampionBuild = {
      build_id: {
        primary_tags_f3pie: "",
      },
      build_tags: [],
      patch: "",
      champion_id: 0,
      championId: 0,
      matchupChampionId: 0,
      rank_plus: "",
      build_tier: "",
      coreItems: [],
      situationalItems: [],
      startingItems: [],
      runes: [],
      keystone: [],
      shards: {
        defenseShard: [],
        flexShard: [],
        offenseShard: [],
      },
      summonerSpells: [],
      skillOrders: [],
      games: 0,
      wins: 0,
      pick_rate: 0,
      misc: {},
      name_tags: [],
    };

    for (const column of columns) {
      if (PROPERTIES_TO_SKIP[column.name]) continue;
      const value: string | null = b[column.position] as unknown as string;
      result[column.name] = castType(column.typeName, value);
    }

    result.championId = result.champion_id;

    return result;
  });

  return builds.filter(validateBuildData);
});

export default LolChampionBuildsModel;

export type LeagueChampionBuild = FromModel<typeof Build>;

const PROPERTIES_TO_SKIP = {
  champion_build_norm_tag_weights: true,
  champion_norm_tag_weights: true,
  champion_vs_champion_build_tag_weights_diff: true,
  champion_vs_champion_norm_tag_weights_diff: true,
};

// Validates that a build has valid data that can be actually used
function validateBuildData(build: LeagueChampionBuild) {
  const { keystone, runes, shards, summonerSpells } = build;

  const hasRunes = !!runes.filter((rune) => rune.runeId).length;
  const hasKeystones = !!keystone.filter((keystone) => keystone.keystone_id)
    .length;
  const hasDefShards = !!shards.defenseShard.filter((shard) => shard.shard_id)
    .length;
  const hasFlexShards = !!shards.flexShard.filter((shard) => shard.shard_id)
    .length;
  const hasOffShards = !!shards.offenseShard.filter((shard) => shard.shard_id)
    .length;
  const hasShards = hasDefShards && hasFlexShards && hasOffShards;
  const hasSpells = !!summonerSpells.filter(
    (set) => set.summonerSpellIds.length,
  ).length;

  return hasKeystones && hasRunes && hasShards && hasSpells;
}

function castType(typeText: string, value: string) {
  switch (typeText) {
    case "BOOLEAN":
      return value === "true";
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
    case "INT":
      return value ? Number(value) : 0;
    case "MAP":
      return value ? JSON.parse(value) : {};
    case "ARRAY":
      return value ? JSON.parse(value) : [];
    case "STRUCT":
      return value ? JSON.parse(value) : {};
    case "STRING":
    case undefined:
    default:
      return value ?? "";
  }
}
